import { PermissionEnum } from '@Application/Security/Roles';
import type AiCostLimitAlertView from '@UI/Store/AiCostLimitAlerts/Views/AiCostLimitAlertView';
import useAiCostLimitSubjects from '@UI/Hooks/useAiCostLimitSubjects';
import useCurrentOrganization from '@UI/Providers/CurrentOrganizationProvider/useCurrentOrganization';
import useHasPermission from '@UI/Providers/PermissionProvider/useHasPermission';
import * as React from 'react';

export type SubjectNameResolver = (level: 'organization' | 'facility', subjectId: number) => string | null;

/**
 * Resolves the name of a structure or agency: the caller's own assignments first, then the limitable
 * subjects of the account. That list is only fetched when the caller may read it and an alert names a
 * subject the assignments do not know, and only once the assignments are resolved. While it loads, or if it fails, such names stay unknown.
 */
const useSubjectNameResolver = (
	alerts: readonly Pick<AiCostLimitAlertView, 'level' | 'subjectId'>[] | null,
	accountId: number | null,
): SubjectNameResolver => {
	const { organizations, isResolved } = useCurrentOrganization();
	const canReadSubjects = useHasPermission(PermissionEnum.AI_COST_LIMIT_WRITE);

	const findAssignedName = React.useCallback<SubjectNameResolver>(
		(level, subjectId) => {
			if (level === 'organization') {
				return organizations.find((organization) => organization.id === subjectId)?.name ?? null;
			}

			return (
				organizations
					.flatMap((organization) => organization.facilities)
					.find((facility) => facility.id === subjectId)?.name ?? null
			);
		},
		[organizations],
	);

	const hasUnassignedSubject = React.useMemo(
		() =>
			isResolved &&
			(alerts ?? []).some(
				(alert) =>
					(alert.level === 'organization' || alert.level === 'facility') &&
					findAssignedName(alert.level, alert.subjectId) === null,
			),
		[alerts, findAssignedName, isResolved],
	);

	const { data: subjects } = useAiCostLimitSubjects(accountId, canReadSubjects && hasUnassignedSubject);

	return React.useCallback<SubjectNameResolver>(
		(level, subjectId) => {
			const assignedName = findAssignedName(level, subjectId);

			if (assignedName !== null) {
				return assignedName;
			}

			if (subjects === null || subjects.accountId !== accountId) {
				return null;
			}

			return subjects.findName(level, subjectId);
		},
		[findAssignedName, subjects, accountId],
	);
};

export default useSubjectNameResolver;
